<template>
  <section class="composicao-pedido">
    <div class="d-flex flex-wrap align-center justify-space-between mb-3">
      <div>
        <h3 class="text-h5 font-weight-bold mb-1">{{ $t("Composição") }}</h3>
        <p class="text-body-2 mb-0">
          {{ $t("composicaoPedido.instrucao") }}
        </p>
      </div>
      <strong aria-live="polite">
        {{ $tc("composicaoPedido.itens", totalItens, { n: totalItens }) }}
        <span v-for="total in totaisPorMoeda" :key="total.codigo">
          · {{ dinheiro(total.valor, total.codigo) }}
        </span>
      </strong>
    </div>
    <div
      v-if="erroCatalogo"
      class="composicao-aviso composicao-aviso--erro"
      role="alert"
    >
      {{ $t("composicaoPedido.erroCatalogo") }}
    </div>
    <div v-else-if="carregandoMoedas" class="composicao-aviso">
      {{ $t("composicaoPedido.carregando") }}
    </div>
    <template v-else>
      <div class="d-flex flex-wrap align-center mb-3">
        <v-btn-toggle v-model="tipo" mandatory dense class="mr-4 mb-2">
          <v-btn value="CEDULA" class="font-weight-bold text-none">{{
            $t("Cédulas")
          }}</v-btn>
          <v-btn value="MOEDA" class="font-weight-bold text-none">{{
            $t("Moedas")
          }}</v-btn>
        </v-btn-toggle>
        <v-select
          v-if="paises.length > 1"
          :value="paisSelecionado"
          @change="paisId = $event"
          :items="paises"
          :label="$t('Pais')"
          outlined
          dense
          hide-details
          class="filtro mb-2 mr-3"
        />
        <v-select
          v-if="familiasDisponiveis"
          v-model="familia"
          :items="familias"
          :label="$t('composicaoPedido.familia')"
          outlined
          dense
          hide-details
          class="filtro mb-2"
        />
      </div>
      <v-simple-table class="grade-table">
        <thead>
          <tr>
            <th>{{ $t("Denominação") }}</th>
            <th class="text-right">{{ $t("Quantidade de unidades") }}</th>
            <th class="text-right">{{ $t("Total") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(moeda, index) in visiveis"
            :key="moeda.id"
            :class="{ 'linha-preenchida': quantidade(moeda) > 0 }"
          >
            <td>
              <strong>{{ dinheiroMoeda(moeda.valorFacial, moeda) }}</strong
              ><small class="d-block">{{ moeda.descricao }}</small>
            </td>
            <td>
              <v-text-field
                :ref="`quantidade-${index}`"
                :value="quantidade(moeda)"
                type="text"
                placeholder="0"
                inputmode="numeric"
                pattern="[0-9]*"
                autocomplete="off"
                outlined
                dense
                hide-details="auto"
                class="quantidade-input"
                :aria-label="
                  $t('Quantidade de {arg1}', { arg1: moeda.descricao })
                "
                :error-messages="
                  erro(moeda)
                    ? $t('Use uma quantidade inteira entre 0 e 2.147.483.647.')
                    : ''
                "
                @input="alterar(moeda, $event)"
                @focus="$event.target.select()"
                @keydown.enter.exact.prevent="avancar(index)"
              />
            </td>
            <td class="text-right font-weight-bold">
              {{ dinheiroMoeda(total(moeda), moeda) }}
            </td>
          </tr>
          <tr v-if="!visiveis.length">
            <td colspan="3">
              {{ mensagemVazia }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
      <div
        class="d-flex justify-space-between flex-wrap mt-3 text-body-2"
        aria-live="polite"
      >
        <span>{{
          $t("{arg1} denominação(ões) preenchida(s) nesta grade", {
            arg1: visiveis.filter((m) => quantidade(m) > 0).length,
          })
        }}</span>
        <strong>{{
          $t("Subtotal desta grade: {arg1}", {
            arg1: dinheiro(subtotalVisivel, codigoMoedaSelecionada),
          })
        }}</strong>
      </div>
      <div v-if="ocultos.length" class="composicao-aviso mt-3">
        {{ $t("composicaoPedido.ocultos", { n: ocultos.length }) }}
      </div>
      <v-simple-table v-if="ocultos.length" dense class="mt-2">
        <tbody>
          <tr v-for="(item, index) in ocultos" :key="index">
            <td>{{ descricao(item) }}</td>
            <td class="text-right">
              {{
                $tc("composicaoPedido.unidades", item.quantidade, {
                  n: item.quantidade,
                })
              }}
            </td>
            <td class="text-right">
              <v-btn
                icon
                :aria-label="
                  $t('composicaoPedido.remover', { descricao: descricao(item) })
                "
                @click="remover(item)"
                ><v-icon>mdi-delete-outline</v-icon></v-btn
              >
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </template>
  </section>
</template>

<script>
export default {
  name: "ComposicaoPedido",
  props: {
    value: { type: Array, default: () => [] },
    moedas: { type: Array, default: () => [] },
    erroCatalogo: Boolean,
    carregandoMoedas: Boolean,
    paisInquilinoId: { type: Number, default: null },
    codigoMoeda: { type: String, default: "BRL" },
  },
  data: () => ({
    tipo: "CEDULA",
    paisId: null,
    familia: "TODAS",
  }),
  computed: {
    familias() {
      return [
        { text: this.$t("Segunda família"), value: "SEGUNDA" },
        { text: this.$t("Primeira família"), value: "PRIMEIRA" },
        { text: this.$t("Todas as famílias"), value: "TODAS" },
      ];
    },
    paises() {
      return [
        ...new Map(
          this.moedas
            .filter((m) => m.paisId != null)
            .map((m) => [
              Number(m.paisId),
              { text: m.paisNome || m.paisSigla, value: Number(m.paisId) },
            ])
        ).values(),
      ];
    },
    paisSelecionado() {
      const paisInquilino = Number(this.paisInquilinoId);
      return (
        this.paisId ||
        (this.paises.some((pais) => pais.value === paisInquilino)
          ? paisInquilino
          : this.paises[0]?.value) ||
        null
      );
    },
    moedasDoPais() {
      return this.moedas.filter(
        (moeda) => Number(moeda.paisId) === Number(this.paisSelecionado)
      );
    },
    familiasDisponiveis() {
      return this.moedasDoPais.filter((m) => m.familia).length;
    },
    visiveis() {
      return this.moedasDoPais
        .filter(
          (m) =>
            String(m.tipoMoeda).toUpperCase() === this.tipo &&
            (!this.familiasDisponiveis ||
              this.familia === "TODAS" ||
              m.familia === this.familia ||
              !m.familia)
        )
        .slice()
        .sort(
          (a, b) =>
            Number(b.valorFacial) - Number(a.valorFacial) ||
            a.descricao.localeCompare(b.descricao)
        );
    },
    mensagemVazia() {
      if (!this.moedas.length) {
        return this.$t("composicaoPedido.semMoedas");
      }
      return this.$t("composicaoPedido.semDenominacoes");
    },
    ocultos() {
      return this.value.filter(
        (item) =>
          !this.visiveis.some(
            (m) =>
              Number(item.operacaoMoedaId || item.operacaoMoeda?.id) === m.id
          )
      );
    },
    totalItens() {
      return this.value.filter((item) => Number(item.quantidade) > 0).length;
    },
    totaisPorMoeda() {
      const totais = new Map();
      this.value.forEach((item) => {
        const moeda = this.moedas.find(
          (m) => m.id === Number(item.operacaoMoedaId || item.operacaoMoeda?.id)
        );
        const codigo = this.codigoDaMoeda(moeda);
        totais.set(codigo, (totais.get(codigo) || 0) + this.valor(item));
      });
      return [...totais].map(([codigo, valor]) => ({ codigo, valor }));
    },
    codigoMoedaSelecionada() {
      return this.codigoDaMoeda(this.visiveis[0]);
    },
    subtotalVisivel() {
      return this.visiveis.reduce((sum, moeda) => sum + this.total(moeda), 0);
    },
  },
  watch: {
    moedas: {
      immediate: true,
      handler() {
        this.$nextTick(this.ajustarFiltros);
      },
    },
    paisInquilinoId() {
      this.$nextTick(this.ajustarFiltros);
    },
  },
  methods: {
    codigoDaMoeda(moeda) {
      if (!moeda) {
        return this.codigoMoeda;
      }
      const valor = String(moeda.paisMoeda || "")
        .trim()
        .toUpperCase();
      const mesmoPais = Number(moeda.paisId) === Number(this.paisInquilinoId);
      const codigoInquilino = String(this.codigoMoeda || "")
        .trim()
        .toUpperCase();
      if ((mesmoPais && codigoInquilino === "BRL") || valor === "BRL") {
        return "BRL";
      }
      if (
        this.moedas.some(
          (item) =>
            Number(item.paisId) === Number(moeda.paisId) &&
            String(item.paisMoeda || "")
              .trim()
              .toUpperCase() === "BRL"
        )
      ) {
        return "BRL";
      }
      if (valor === "REAL") {
        return "BRL";
      }
      const nomes = {
        DOLAR: "USD",
        DÓLAR: "USD",
        EURO: "EUR",
        BOLIVAR: "VES",
        BOLÍVAR: "VES",
      };
      if (nomes[valor]) {
        return nomes[valor];
      }
      if (mesmoPais) {
        return this.codigoMoeda;
      }
      return valor || this.codigoMoeda;
    },
    ajustarFiltros() {
      if (
        !this.moedasDoPais.some(
          (moeda) => String(moeda.tipoMoeda).toUpperCase() === this.tipo
        )
      ) {
        const primeiroTipo = this.moedasDoPais.find((moeda) =>
          ["CEDULA", "MOEDA"].includes(String(moeda.tipoMoeda).toUpperCase())
        );
        if (primeiroTipo)
          this.tipo = String(primeiroTipo.tipoMoeda).toUpperCase();
      }
    },
    item(moeda) {
      return this.value.find(
        (item) =>
          Number(item.operacaoMoedaId || item.operacaoMoeda?.id) === moeda.id
      );
    },
    quantidade(moeda) {
      const item = this.item(moeda);
      return item?._quantidadeInvalida
        ? item._textoQuantidade
        : item?.quantidade ?? "";
    },
    erro(moeda) {
      return !!this.item(moeda)?._quantidadeInvalida;
    },
    valor(item) {
      const moeda = this.moedas.find(
        (m) => m.id === Number(item.operacaoMoedaId || item.operacaoMoeda?.id)
      );
      return (
        Number(item.quantidade || 0) *
        Number(
          moeda?.valorFacial ??
            item.operacaoMoedaValorFacial ??
            item.operacaoMoeda?.valorFacial ??
            item.valorUnitario ??
            0
        )
      );
    },
    total(moeda) {
      return this.valor(this.item(moeda) || {});
    },
    dinheiro(valor, codigo = this.codigoMoeda) {
      try {
        return new Intl.NumberFormat(this.$i18n.locale, {
          style: "currency",
          currency: codigo,
        }).format(valor);
      } catch (_) {
        return `${Number(valor).toLocaleString(this.$i18n.locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${codigo}`;
      }
    },
    dinheiroMoeda(valor, moeda) {
      return this.dinheiro(valor, this.codigoDaMoeda(moeda));
    },
    descricao(item) {
      return (
        item.operacaoMoedaDescricao ||
        item.operacaoMoeda?.descricao ||
        item.moedaDescricao ||
        item.desc ||
        item.tipoMoeda ||
        this.$t("Composição")
      );
    },
    alterar(moeda, texto) {
      const quantidade = String(texto).trim();
      const valido =
        /^\d*$/.test(quantidade) && Number(quantidade) <= 2147483647;
      const itens = this.value.filter(
        (item) =>
          Number(item.operacaoMoedaId || item.operacaoMoeda?.id) !== moeda.id
      );
      if (!valido || Number(quantidade) > 0)
        itens.push({
          tipoMoeda: null,
          operacaoMoedaId: moeda.id,
          operacaoMoedaDescricao: moeda.descricao,
          operacaoMoedaValorFacial: moeda.valorFacial,
          quantidade: valido ? Number(quantidade) : null,
          _textoQuantidade: valido ? undefined : texto,
          _quantidadeInvalida: !valido,
        });
      this.$emit("input", itens);
    },
    remover(item) {
      this.$emit(
        "input",
        this.value.filter((atual) => atual !== item)
      );
    },
    avancar(index) {
      const campo = this.$refs[`quantidade-${index + 1}`];
      (Array.isArray(campo) ? campo[0] : campo)?.focus();
    },
  },
};
</script>

<style scoped>
.filtro {
  max-width: 230px;
}
.quantidade-input {
  max-width: 220px;
  margin-left: auto;
}
.quantidade-input ::v-deep input {
  text-align: right;
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  background: white;
  border: 1px solid #bdc8d5;
  border-radius: 6px;
  padding: 8px 12px;
  min-height: 38px;
}
.quantidade-input ::v-deep input:focus {
  border: 2px solid #3457d5;
  box-shadow: 0 0 0 3px #e8edff;
}
.composicao-pedido ::v-deep .v-btn--active {
  background: #e8edff !important;
  color: #2443b6 !important;
}
.linha-preenchida {
  background: #edf7f0;
}
.grade-table td {
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}
.grade-table small {
  color: #667085;
}
.composicao-aviso {
  padding: 8px 12px;
  border-left: 4px solid #3457d5;
  border-radius: 6px;
  background: #e8edff;
  color: #24324a;
  font-size: 0.875rem;
}
.composicao-aviso--erro {
  border-left-color: #d92d20;
  background: #fef3f2;
  color: #912018;
}
</style>
